import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/hooks'
import moment from 'moment'
import { ROUTES } from '../../../constants'
import { UploadedFile } from 'models/general'
import { ISydDocument, UseStateSimpleCallbackModel } from 'models/shared.models'
import {
  IAccountingProcess,
  IAccountingChecklist,
  IGetAccountingRemindersResponseService,
  TAccountingProcessSteps,
} from 'models/accounting.models'
import RequestedDocument from 'models/documentRequest'
import { toggleChatComponent } from 'lib/helpers/shared.helpers'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import useLoader from 'hooks/Shared/useLoader'
import useUpdateStateFunction from 'hooks/Shared/useUpdateStateFunction'
import useGetAccounting from 'hooks/Accounting/useGetAccounting'
import useGetAccountingIncompleteProcesses from 'hooks/Accounting/useGetAccountingIncompleteProcesses'
import useGetAccountingTasks from 'hooks/Accounting/useGetAccountingTasks'
import useHandleAccountingDocuments, {
  IUseHandleAccountingDocuments,
} from 'hooks/Accounting/useHandleAccountingDocuments'
import useHandleAccountingReminders, {
  IUseHandleAccountingReminders,
} from 'hooks/Accounting/useHandleAccountingReminders'
import useHandleAccountingSteps, {
  IUseHandleAccountingSteps,
} from 'hooks/Accounting/useHandleAccountingSteps'
import useUpdateAccountingRequestedDocuments from 'hooks/Accounting/useUpdateAccountingRequestedDocuments'
import useUpdateAccountingTask from 'hooks/Accounting/useUpdateAccountingTask'
import {
  setActiveDateRange,
  setRefetchAccountingProcess,
  toggleAccountingTaskStatus,
} from 'store/Accounting/accounting.reducer'
import {
  selectAccountingActiveDateRange,
  selectAccountingForm,
  selectAccountingTasks,
  selectCurrentAccountingProcess,
  selectRefetchAccountingProcess,
} from 'store/Accounting/accounting.selectors'
import { setPath } from 'actions/documentation'
import { IAccountingIncompleteProcess } from 'services/Accounting/accounting.models'
import { LoadingContextProps } from 'providers/LoaderProvider/LoaderProvider'
import { toast } from 'react-toastify'
import { errorToastOptions } from 'lib/utils/toast.utils'

export interface IAccountingLocalState {
  accountingReminderEmailSent: string
  isSendAccountingReminderLoading: boolean
  showAccountingReminderSuccess: boolean
  isSendAccountingReminderError: boolean
  accountingHistoryReminders: UseStateSimpleCallbackModel<IGetAccountingRemindersResponseService>
  isAccountingTasksDrawerOpen: boolean
  updateAccountingProcessLoading: boolean
  stepChangeLoading: boolean
  isAccountingIncompleteProcessesDrawerOpen: boolean
}

export interface IUseAccounting
  extends IAccountingLocalState,
    IUseHandleAccountingDocuments,
    Pick<
      IUseHandleAccountingReminders,
      'handleSendAccountingReminders' | 'handleShowAccountingReminders'
    >,
    IUseHandleAccountingSteps {
  activeDateRange: Date
  checklist: IAccountingChecklist[]
  filesToUpload: UploadedFile[]
  filesInProcess: ISydDocument[]
  hasIncompleteProcesses: boolean
  isAllAccountingTasksComplete: boolean
  loader: LoadingContextProps
  requiredFileList: RequestedDocument[]
  stepThreeComments: {
    text: string
    type: number
  }[]
  accountingIncompleteProcesses?: IAccountingIncompleteProcess[]
  currentAccountingProcess?: IAccountingProcess
  changeDateRangeHandler: (date: Date) => void
  checkForExistingFileNameInAccountingProcess: (
    file: UploadedFile[] | string
  ) => boolean
  fileUploadHandlerFactory: (
    step: TAccountingProcessSteps,
    maxUploadsNumber: number
  ) => (
    totalUploadItems: ISydDocument[],
    additionalValidFiles?: ISydDocument[],
    additionalInvalidFiles?: ISydDocument[]
  ) => (files: UploadedFile[]) => void
  goToDocumentation: () => void
  toggleAccountingIncompleteProcessesDrawerHandler: () => void
  toggleAccountingTasksDrawerHandler: () => void
  toggleAccountingTaskStatusHandler: (id: number) => void
}

const useAccounting = (): IUseAccounting => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const loader = useLoader()

  const activeDateRange = useAppSelector(selectAccountingActiveDateRange)
  const accountingForm = useAppSelector(selectAccountingForm)
  const accountingTasks = useAppSelector(selectAccountingTasks)
  const currentAccountingProcess = useAppSelector(selectCurrentAccountingProcess)
  const shouldRefetchAccountingProcess = useAppSelector(
    selectRefetchAccountingProcess
  )
  const workspaceId = useAppSelector(selectActiveWorkspace)

  const checklist = currentAccountingProcess?.checklist ?? []
  const filesInProcess = currentAccountingProcess?.uploadedDocuments ?? []
  const filesToUpload = accountingForm.files
  const stepThreeComments = currentAccountingProcess?.commentsList ?? []

  const [accountingState, setAccountingState] = useState<IAccountingLocalState>(
    {
      accountingReminderEmailSent: '',
      isSendAccountingReminderLoading: false,
      showAccountingReminderSuccess: false,
      isSendAccountingReminderError: false,
      accountingHistoryReminders: {
        isLoading: false,
        isError: false,
      },
      isAccountingTasksDrawerOpen: false,
      updateAccountingProcessLoading: false,
      stepChangeLoading: false,
      isAccountingIncompleteProcessesDrawerOpen: false,
    }
  )

  const {
    isAccountingTasksDrawerOpen,
    isAccountingIncompleteProcessesDrawerOpen,
  } = accountingState

  const updateAccountingState =
    useUpdateStateFunction<IAccountingLocalState>(setAccountingState)

  const isAllAccountingTasksComplete = accountingTasks?.every(t => t.checked)

  const { refetch: refetchAccountingTasks } = useGetAccountingTasks(
      workspaceId,
      new Date(activeDateRange)
  )

  useEffect(() => {
    if (workspaceId) {
      refetchAccountingTasks()
    }
  }, [workspaceId, refetchAccountingTasks])

  const { isLoading: mainLoading, refetch: getAccounting } = useGetAccounting(
    workspaceId,
    new Date(activeDateRange),
    currentAccountingProcess?.id
  )

  useUpdateAccountingRequestedDocuments({
    workspaceId,
    currentAccountingProcess,
    list: [4, 17, 19, 18, 20, 21],
    enabled: currentAccountingProcess?.requiredDocuments?.length === 0,
    getAccounting,
  })

  const {
    hasIncompleteProcesses,
    query: getAccountingIncompleteProcessesQuery,
  } = useGetAccountingIncompleteProcesses(workspaceId)

  const {
    isLoading: accountingIncompleteProcessesLoading,
    data: accountingIncompleteProcesses,
  } = getAccountingIncompleteProcessesQuery

  const {
    requiredFileList,
    handleFileUpload,
    handleFileRetryUpload,
    handleFileDelete,
    handleFileRename,
    handleFileValidation,
    handleFileDownload,
    handleEditRequiredFileList,
  } = useHandleAccountingDocuments({
    workspaceId,
    currentAccountingProcess,
    getAccounting,
  })

  const {
    handleGetAccountingReminders,
    handleSendAccountingReminders,
    handleShowAccountingReminders,
  } = useHandleAccountingReminders({
    workspaceId,
    accountingReduxId: currentAccountingProcess?.id,
    updateAccountingState,
  })

  const {
    activeStep,
    goToStep,
    handleStepBack,
    handleStepSubmit,
    handleProcessCheck,
    runValidationServiceOnSelectedFiles,
  } = useHandleAccountingSteps({
    workspaceId,
    currentAccountingProcess,
    updateAccountingState,
    getAccounting,
    handleFileDelete,
    handleFileValidation,
  })

  const toggleAccountingTaskStatusMutation =
    useUpdateAccountingTask(workspaceId)

  const changeDateRangeHandler = useCallback(
    (date: Date) => dispatch(setActiveDateRange(date)),
    [dispatch]
  )

  const checkForExistingFileNameInAccountingProcess = (
    file: UploadedFile[] | string
  ) =>
    Array.isArray(file)
      ? file.filter(item => filesInProcess.some(f => f.name === item.name))
          .length > 0
      : filesInProcess.some(f => f.name === file)

  const fileUploadHandlerFactory = (
    step: TAccountingProcessSteps,
    maxUploadsNumber: number
  ) => {
    return (
        totalUploadItems: ISydDocument[],
        additionalValidFiles?: ISydDocument[],
        additionalInvalidFiles?: ISydDocument[]
      ) =>
      (files: UploadedFile[]) => {
        if (checkForExistingFileNameInAccountingProcess(files)) {
          toast.error(
            'No puedes subir más de un archivo con el mismo nombre durante el proceso',
            errorToastOptions
          )
          return
        }

        if (totalUploadItems.length >= maxUploadsNumber) {
          toast.error(
            `No puedes subir más de ${maxUploadsNumber} archivos por documento`,
            errorToastOptions
          )

          return
        }

        runValidationServiceOnSelectedFiles(
          step,
          additionalValidFiles ?? [],
          additionalInvalidFiles ?? []
        )

        handleFileUpload(files)
      }
  }

  const goToDocumentation = () => {
    if (!currentAccountingProcess) return
    const year = `${currentAccountingProcess.year}`
    const month = `${moment(currentAccountingProcess.month, 'M').format('MMMM').charAt(0).toUpperCase() + moment(currentAccountingProcess.month, 'M').format('MMMM').slice(1)}`
    dispatch(
      setPath([
        { id: 0, label: 'Documentos', goTo: '' },
        { id: 1, label: 'Contabilidad', goTo: 'Contabilidad' },
        { id: 2, label: year, goTo: year },
        { id: 3, label: month, goTo: month },
      ])
    )
    navigate(ROUTES.DOCUMENTATION)
  }

  const toggleAccountingIncompleteProcessesDrawerHandler = () => {
    updateAccountingState(prevState => ({
      ...prevState,
      isAccountingIncompleteProcessesDrawerOpen:
        !prevState.isAccountingIncompleteProcessesDrawerOpen,
    }))
  }

  const toggleAccountingTasksDrawerHandler = () => {
    updateAccountingState(prevState => ({
      ...prevState,
      isAccountingTasksDrawerOpen: !prevState.isAccountingTasksDrawerOpen,
    }))
  }

  const toggleAccountingTaskStatusHandler = (id: number) => {
    const taskToUpdate = accountingTasks.find(t => t.idTask === id)

    if (!taskToUpdate) return

    dispatch(toggleAccountingTaskStatus(id))

    toggleAccountingTaskStatusMutation.mutate({
      ...taskToUpdate,
      checked: !taskToUpdate.checked,
    })
  }

  useEffect(() => {
    mainLoading || accountingIncompleteProcessesLoading
      ? loader.primary('Cargando información')
      : loader.close()
  }, [loader, mainLoading, accountingIncompleteProcessesLoading])

  useEffect(() => {
    handleGetAccountingReminders()
  }, [handleGetAccountingReminders])

  useEffect(() => {
    if (shouldRefetchAccountingProcess) {
      getAccounting()
      dispatch(setRefetchAccountingProcess(false))
    }
  }, [dispatch, shouldRefetchAccountingProcess, getAccounting])

  useEffect(() => {
    isAccountingTasksDrawerOpen || isAccountingIncompleteProcessesDrawerOpen
      ? toggleChatComponent(false)
      : toggleChatComponent(true)
  }, [isAccountingTasksDrawerOpen, isAccountingIncompleteProcessesDrawerOpen])

  return {
    ...accountingState,
    activeDateRange,
    activeStep,
    accountingIncompleteProcesses,
    checklist,
    currentAccountingProcess,
    filesInProcess,
    filesToUpload,
    hasIncompleteProcesses,
    isAllAccountingTasksComplete,
    loader,
    requiredFileList,
    stepThreeComments,
    changeDateRangeHandler,
    checkForExistingFileNameInAccountingProcess,
    fileUploadHandlerFactory,
    goToDocumentation,
    goToStep,
    handleEditRequiredFileList,
    handleFileDelete,
    handleFileDownload,
    handleFileRename,
    handleFileRetryUpload,
    handleFileUpload,
    handleFileValidation,
    handleProcessCheck,
    handleSendAccountingReminders,
    handleShowAccountingReminders,
    handleStepBack,
    handleStepSubmit,
    runValidationServiceOnSelectedFiles,
    toggleAccountingTasksDrawerHandler,
    toggleAccountingTaskStatusHandler,
    toggleAccountingIncompleteProcessesDrawerHandler,
  }
}

export default useAccounting
